import React from 'react'
// libs
import { matchPath } from 'react-router'
import { useRouter } from 'hooks/useRouter'
import { InjectedFormProps, reduxForm } from 'redux-form'
// components
import { ReduxInput, ReduxSelect } from 'components/FormElements/ReduxForm'
// context
import { LinkedItemsAPIContext } from 'containers/LinkedItems/layers/API'
import { LinkedItemsActionsContext } from 'containers/LinkedItems/layers/Business/LinkedItemsActions'
// constants
import { ENTITIES, Forms, Modals } from 'constants/index'
// hooks
import { useFormDirtyCount, useShowModal } from 'hooks'
// types
import { IOption } from 'components/FormElements/Select'
import { IPlan, IPlanAction, IPlanItem, IPlanKpi, IPlanObjective } from 'types/graphql'
// views
import {
  Button,
  ButtonsWrapper,
  CommentForm,
  Counter,
  Details,
  LinkIcon,
  LinkIconWithCounter,
  Paper,
  Path,
  Title,
  Wrapper
} from './view'
// utils
import get from 'lodash/get'
import { safeGetOr } from 'utils/fp'
import { getIconByEntity } from 'utils/entity'
import { filter, map, pipe } from 'lodash/fp'
import { getValueFromOption } from 'utils/form'

// types
interface IIncomingProps {
  disabled?: boolean
  dirtyFieldsCount: number
  entity?: IPlan | IPlanAction | IPlanKpi | IPlanObjective
  handleCloseSearch?: () => void
  isGlobalSearch?: boolean
  query: any
  source: any
  variables: { filter: { planId: string } }
}

interface IFormData {
  search: string
}

type IFormProps = InjectedFormProps<IFormData>

type IProps = IIncomingProps & IFormProps

export const LINK_WEIGHTING_OPTIONS = [
  { label: 'Must do first', value: 55 },
  {
    label: 'Significantly contributes towards',
    value: 30
  },
  { label: 'Contributes towards', value: 10 },
  { label: 'Linked with', value: 5 }
]

const LinkedItem: React.FunctionComponent<IProps> = ({
  entity,
  handleCloseSearch,
  handleSubmit,
  isGlobalSearch,
  query,
  source,
  variables,
  disabled
}) => {
  const router = useRouter()

  const { updateLinkedItem } = React.useContext(LinkedItemsAPIContext)

  const {
    handleObjectiveClick,
    handleActionClick,
    handleKPIClick,
    handlePlanClick,
    onUnlinkButtonClick,
    onLinkButtonClick,
    onMakeParentButtonClick
  } = React.useContext(LinkedItemsActionsContext)

  const { showModal: showConfirmUnlinkModal } = useShowModal({
    modalType: Modals.CONFIRM_UNLINK_PLAN_ITEM_MODAL
  })

  const { dirtyFieldsCount } = useFormDirtyCount(`LINKED_ITEM_COMMENT_FORM_${source.id}`)

  // constants
  const isPlan = source.type === ENTITIES.PLAN
  const isPlanAction = source.type === ENTITIES.PLAN_ACTION
  const isPlanKPI = source.type === ENTITIES.PLAN_KPI
  const isPlanObjective = source.type === ENTITIES.PLAN_OBJECTIVE

  const linkingItemsCount =
    get(source, 'destinationLinkingItems', []).length + get(source, 'sourceLinkingItems', []).length

  const sourcePlan: IPlanItem = pipe(
    safeGetOr<any, any, any>([], 'planItems.data'),
    filter((planItem: IPlanItem) => {
      if (isPlanAction) {
        return planItem.planActionId === source.id && !planItem.referenced
      }
      if (isPlanKPI) {
        return planItem.planKPIId === source.id && !planItem.referenced
      }
      if (isPlanObjective) {
        return planItem.planObjectiveId === source.id && !planItem.referenced
      }
    }),
    map((entity) => ({ id: entity.plan.id, referenced: entity.referenced })),
    safeGetOr<any, any, any>({}, '[0]')
  )(source)

  const shouldShowMakeParentButton =
    isPlan &&
    source.id !== router.query.id &&
    source.id !== router.query.parentId &&
    source.parentPlanId !== router.query.id &&
    !!matchPath('/plans/:companyId/edit/:id', router.pathname)

  const LINK_PARENT_OPTIONS = isGlobalSearch
    ? []
    : [
        { label: source.description, value: source.id },
        { label: get(entity, 'description'), value: entity.id }
      ]

  const { planLinkObjects, actionLinkObjects, kpiLinkObjects, objectiveLinkObjects } = get(
    entity,
    'currentPermissions',
    {}
  ) as any

  const shouldShowLinkButton = !isPlan && !!matchPath('/plans/:companyId/edit/:id', router.pathname)
  const shouldShowUnlinkButton = planLinkObjects || actionLinkObjects || kpiLinkObjects || objectiveLinkObjects
  const shouldShowViewButton = (isPlan && router.query.id !== source.id) || !isPlan

  // methods
  const handleFormSubmit = (data) => {
    const input = {
      description: data.comment,
      weighting: getValueFromOption(data.weighting),
      parentId: getValueFromOption(data.parent)
    }

    updateLinkedItem(source.linkItemId, input, query, variables)
  }

  const handleViewButtonClick = () => {
    if (isPlanObjective) {
      handleObjectiveClick(source.id)
    }

    if (isPlanAction) {
      handleActionClick(source.id)
    }

    if (isPlanKPI) {
      handleKPIClick(source.id)
    }

    if (isPlan) {
      handlePlanClick(source.id, source.parentPlanId, source.companyId)
      handleCloseSearch()
    }
  }

  const handleLinkButtonClick = () => {
    onLinkButtonClick({ ...source, planId: sourcePlan.id })
    handleCloseSearch()
  }

  const handleUnlinkButtonClick = (event) => {
    event.stopPropagation()

    showConfirmUnlinkModal({
      handleConfirm: onUnlinkButtonClick(source.linkItemId, query, variables),
      title: source.description
    })
  }

  const handleMakeParentButtonClick = () =>
    onMakeParentButtonClick(router.query.id as string, source.id, handleCloseSearch)

  return (
    <Paper isGlobalSearch={isGlobalSearch} component={'form'} onSubmit={handleSubmit(handleFormSubmit)}>
      <Wrapper container alignItems={'center'} direction={'row'}>
        <Details container wrap={'nowrap'}>
          {getIconByEntity(source.type)}
          <Title isGlobalSearch={isGlobalSearch}>{source.description || source.name}</Title>
        </Details>

        {!isGlobalSearch && (
          <CommentForm container direction={'row'}>
            {/* <ReduxSelect isDisabled={disabled} label='Parent' name='parent' options={LINK_PARENT_OPTIONS} /> */}
            {/* <ReduxSelect
              name='weighting'
              label='Weighting'
              isDisabled={disabled}
              options={LINK_WEIGHTING_OPTIONS as unknown as IOption[]}
            /> */}
            <ReduxInput disabled={disabled} multiline name='comment' label='Relationship comments...' />
          </CommentForm>
        )}

        <Path>{source.path && source.path}</Path>

        <ButtonsWrapper container direction={'row'} isGlobalSearch={isGlobalSearch}>
          {isGlobalSearch ? (
            <>
              {shouldShowViewButton && (
                <Button type={'button'} onClick={handleViewButtonClick}>
                  VIEW
                </Button>
              )}
              {shouldShowLinkButton && (
                <Button type={'button'} onClick={handleLinkButtonClick}>
                  LINK
                </Button>
              )}
              {shouldShowMakeParentButton && (
                <Button type={'button'} onClick={handleMakeParentButtonClick}>
                  MAKE PARENT
                </Button>
              )}
            </>
          ) : (
            <>
              {shouldShowUnlinkButton && (
                <Button disabled={disabled} type={'button'} onClick={handleUnlinkButtonClick}>
                  UNLINK
                </Button>
              )}
              <Button type={'button'} onClick={handleViewButtonClick}>
                VIEW
              </Button>
            </>
          )}

          {!!dirtyFieldsCount && <Button type={'submit'}>SAVE</Button>}

          {linkingItemsCount > 0 && (
            <LinkIconWithCounter>
              <Counter>{linkingItemsCount}</Counter>
              <LinkIcon isLinked isGlobalSearch={isGlobalSearch} />
            </LinkIconWithCounter>
          )}
        </ButtonsWrapper>
      </Wrapper>
    </Paper>
  )
}

export default reduxForm<any>({
  form: Forms.LINKED_ITEM_COMMENT_FORM,
  enableReinitialize: true
})(LinkedItem)
